/**
 * Download methods for FileStorageService
 */

import { FileCategory } from '../../models/FileStorage';
import { API_ENDPOINTS } from '../../config/api';
import { apiClient } from '../api/apiClient';
import { FILE_OPERATION_TIMEOUT } from './constants';

/**
 * Download specific file by filename
 *
 * @param projectId - Project identifier
 * @param category - File category
 * @param filename - Full filename with version
 * @returns File blob
 */
export async function downloadFile(
  projectId: string,
  category: FileCategory,
  filename: string,
  mapId?: string
): Promise<Blob> {
  try {
    const endpoint = API_ENDPOINTS.FILE_STORAGE.DOWNLOAD(projectId, category, filename, mapId);


    const response = await apiClient.get(endpoint, {
      responseType: 'blob',
      timeout: FILE_OPERATION_TIMEOUT,
    });


    return response.data;
  } catch (error) {
    console.error('[FileStorageService] Download failed:', error);
    throw error;
  }
}

/**
 * Download latest version of a file
 *
 * @param projectId - Project identifier
 * @param category - File category
 * @param originalName - Original filename without version
 * @returns File blob (latest version)
 */
export async function downloadLatest(
  projectId: string,
  category: FileCategory,
  originalName: string
): Promise<Blob> {
  try {
    const endpoint = API_ENDPOINTS.FILE_STORAGE.DOWNLOAD_LATEST(
      projectId,
      category,
      originalName
    );


    const response = await apiClient.get(endpoint, {
      responseType: 'blob',
    });


    return response.data;
  } catch (error) {
    console.error('[FileStorageService] Download latest failed:', error);
    throw error;
  }
}

/**
 * Download multiple files as ZIP archive
 *
 * @param projectId - Project identifier
 * @param category - File category
 * @param filenames - Array of filenames to download
 * @returns ZIP file blob
 */
export async function downloadMultiple(
  projectId: string,
  category: FileCategory,
  filenames: string[]
): Promise<Blob> {
  try {
    const endpoint = API_ENDPOINTS.FILE_STORAGE.DOWNLOAD_MULTIPLE(projectId, category);

    const response = await apiClient.post(endpoint, { filenames }, {
      responseType: 'blob',
    });

    return response.data;
  } catch (error) {
    console.error('[FileStorageService] Multiple download failed:', error);
    throw error;
  }
}

/**
 * Trigger browser download for a blob
 * Creates temporary download link and triggers click
 *
 * @param blob - File blob to download
 * @param filename - Desired filename for download
 */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('[FileStorageService] Browser download failed:', error);
    throw error;
  }
}
